import * as _ from 'lodash';

import {
  finleyAssert,
  utils as commonUtils,
} from '@finley-technologies/common';
import { CreditAgreement, Util } from '@finley-technologies/finley-types';

import {
  CreditFacilityGQLType,
  RequiredIntermediateFieldsGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import { z } from 'zod/v4';

export type CalcTermsCategory =
  | 'borrowingBaseReport'
  | 'borrowingBaseCash'
  | 'borrowingBaseReceivables'
  | 'metrics'
  | 'configuration';

const CalcTermsDynamicFormulaStruct = z.object({
  creditFacilityName: z.string(),
  creditFacilityId: z.string(),
  loanProductName: z.string(),
  loanProductId: z.string(),
  formula: z.optional(z.nullable(z.string())),
});
export type CalcTermsDynamicFormula = z.infer<
  typeof CalcTermsDynamicFormulaStruct
>;

export type CalcTermsRow = {
  categories: CalcTermsCategory[];
  termKey: string;
  definition: string;
  formula?: string | CalcTermsDynamicFormula[] | null;
};

type CalcTermsIntermediateFields = keyof Pick<
  RequiredIntermediateFieldsGQLType,
  'normalizedisChargedOff' | 'normalizedisDelinquent' | 'normalizedDaysPastDue'
>;

type CalcTermsDynamicFormulas = {
  chargeOffs: CalcTermsDynamicFormula[];
  delinquencies: CalcTermsDynamicFormula[];
  daysPastDue: CalcTermsDynamicFormula[];
};

export const isCalcTermsDynamicFormulaArray = (
  test: any[],
): test is CalcTermsDynamicFormula[] => {
  return test.every(
    (testItem) => CalcTermsDynamicFormulaStruct.safeParse(testItem).success,
  );
};

const calcTermsTableColumnHeaders = [
  'termKey',
  'definition',
  'formula',
  'categories',
];

export const termKeyColumnId = 'termKey';
export const calcTermsTableColumnDefs = _.map(
  calcTermsTableColumnHeaders,
  (header) => {
    return {
      accessorKey: header,
      header:
        header === termKeyColumnId ? 'Term' : commonUtils.toTitleCase(header),
    };
  },
);

// 'categories' columnId is only used for filtering and should never be rendered
export const createColumnVisibility = () => {
  const initialColumnVisibility: { [key: string]: boolean } = {};
  calcTermsTableColumnHeaders.forEach((key) => {
    initialColumnVisibility[key] = true;

    if (key === 'categories') {
      initialColumnVisibility[key] = false;
    }
  });

  return initialColumnVisibility;
};

export const formatCellValue = ({
  columnId,
  value,
}: {
  columnId: string;
  value: any;
}): string | null => {
  if (columnId === termKeyColumnId && typeof value === 'string') {
    return commonUtils.toTitleCase(value);
  }

  return value ? String(value) : null;
};

const baseCalcTermsRows: CalcTermsRow[] = [
  {
    categories: ['borrowingBaseReport'],
    termKey: 'totalAssets',
    definition:
      'The starting total amount of assets in the given borrowing base report. This is comprised of the total exposure from receivables as well as the total exposure from the borrowing base contributing cash accounts.',
    formula:
      'Total Receivable Exposure + Total Eligible Borrowing Base Account Cash',
  },
  {
    categories: ['borrowingBaseReport'],
    termKey: 'totalBorrowingBaseAmount',
    definition:
      'The final combined borrowing base from both receivables and cash after all kickouts and applying of advance rate. This is the final number that funding availability will be tested against.',
    formula:
      'Total Cash Borrowing Base Amount + Total Receivable Borrowing Base Amount',
  },
  {
    categories: ['borrowingBaseReport'],
    termKey: 'reportingDate',
    definition:
      'The day that this borrowing base report represents as the collateral snapshot.',
    formula: '',
  },
  {
    categories: ['borrowingBaseReport'],
    termKey: 'fileVersion',
    definition:
      "The version of the borrowing base report for the given reportingDate. Finley takes the latest version by default to represent that day's collateral snapshot.",
    formula: '',
  },
  {
    categories: ['borrowingBaseReport', 'borrowingBaseReceivables'],
    termKey: 'totalReceivableExposure',
    definition:
      'The total exposure from receivables in the given borrowing base report.',
    formula: '',
  },
  {
    categories: ['borrowingBaseReport', 'borrowingBaseReceivables'],
    termKey: 'effectiveReceivableValuationRate',
    definition:
      'The weighted average valuation rate of the receivables in the given borrowing base report.',
    formula: '',
  },
  {
    categories: ['borrowingBaseReport', 'borrowingBaseReceivables'],
    termKey: 'totalReceivableValuationAmount',
    definition:
      'The total valuation amount of receivables the given borrowing base report, found by applying the effective receivable valuation rate against the total exposure of receivables.',
    formula: 'Total Receivable Exposure * Effective Receivable Valuation Rate',
  },
  {
    categories: ['borrowingBaseReport', 'borrowingBaseReceivables'],
    termKey: 'totalEligibilityKickout',
    definition:
      'The net total amount of eligibility kickout in the given borrowing base report. This is then netted out from the total receivable exposure to get the total eligible exposure.',
    formula: '',
  },
  {
    categories: ['borrowingBaseReport', 'borrowingBaseReceivables'],
    termKey: 'totalEligibleExposure',
    definition:
      'The total eligible exposure of receivables in the given borrowing base report. This is determined by netting out the total eligibility kickout from the exposure from receivables.',
    formula: 'Total Receivable Exposure - Total Eligibility Kickout',
  },
  {
    categories: ['borrowingBaseReport', 'borrowingBaseReceivables'],
    termKey: 'totalConcentrationKickout',
    definition:
      'The net total concentration kickout from the concentration limits in the given borrowing base report.',
    formula: '',
  },
  {
    categories: ['borrowingBaseReport', 'borrowingBaseReceivables'],
    termKey: 'concentrationLimitType',
    definition:
      "The type of concentration limit: either pro-forma or weighted average.\n\nPro-forma concentration limits are a percentage concentration based on the relevant parameters, where the numerator is the concentration receivables total eligible exposure less concentration, and the denominator is the total eligible exposure (unless explicitly stated otherwise).\n\nWeighted average concentration limits are the weighted average of a given receivable base field, where the weight is the receivable's eligible exposure less concentration (unless explicitly stated otherwise).",
    formula: '',
  },
  {
    categories: ['borrowingBaseReport', 'borrowingBaseReceivables'],
    termKey: 'concentrationAmount',
    definition:
      'The total eligible exposure less concentration of receivables that fall under the given concentration parameters. This is only relevant for pro-forma concentration limit types.',
    formula: '',
  },
  {
    categories: ['borrowingBaseReport', 'borrowingBaseReceivables'],
    termKey: 'concentration',
    definition:
      'The concentration that will be tested against the concentration limit. For pro-forma concentration limits, this will be a percentage value. For weighted average concentration limits, this will be the averaged field to compare against the concentration limit.',
    formula: '',
  },
  {
    categories: ['borrowingBaseReport', 'borrowingBaseReceivables'],
    termKey: 'concentrationLimit',
    definition:
      'This is the concentration limit that the concentration will be tested against. There will be excess concentration kickout when the concentration breaches the concentration limit.',
    formula: '',
  },
  {
    categories: ['borrowingBaseReport', 'borrowingBaseReceivables'],
    termKey: 'borrowingBaseReduction',
    definition:
      "This is the amount that Finley's calculation agent reduced for each given receivable due to it breaching the concentration limit.",
    formula: '',
  },
  {
    categories: ['borrowingBaseReport', 'borrowingBaseReceivables'],
    termKey: 'totalEligibleExposureLessConcentrationKickouts',
    definition:
      'The total eligible exposure of receivables less the total concentration kickout. This is the borrowing base from receivables prior to applying the advance rate.',
    formula: 'Total Eligible Exposure - Total Concentration Kickout',
  },
  {
    categories: ['borrowingBaseReport', 'borrowingBaseReceivables'],
    termKey: 'weightedAverageReceivableAdvanceRate',
    definition:
      "The weighted average receivable advance rate across the receivables in the given borrowing base report. Each receivable's borrowing base amount is used as the weighted average.",
    formula: '',
  },
  {
    categories: ['borrowingBaseReport', 'borrowingBaseReceivables'],
    termKey: 'effectiveReceivableAdvanceRate',
    definition:
      'This rate illustrates the impact of relevant exposure reductions from valuation, eligibility, concentration limit, and receivable advance rate adjustments to the starting total receivable exposure for a particular borrowing base portfolio.',
    formula:
      'Total Receivable Borrowing Base Amount / Total Receivable Exposure',
  },
  {
    categories: ['borrowingBaseReport', 'borrowingBaseReceivables'],
    termKey: 'totalReceivableBorrowingBaseAmount',
    definition:
      "The final borrowing base contributions from the receivables in the given borrowing base report. This is the exposure of receivables after kicking out ineligible receivables and excess concentration, then applying each receivable's advance rate.",
    formula:
      'Total Eligible Exposure Less Concentration * Effective Receivable Advance Rate',
  },
  {
    categories: ['borrowingBaseReport', 'borrowingBaseCash'],
    termKey: 'totalEligibleBorrowingBaseAccountCash',
    definition:
      'Total cash that is held in an account that is eligible to contribute to borrowing base. These are the X, Y, Z bank account types [pull from hardcoded constant].',
    formula: '',
  },
  {
    categories: ['borrowingBaseReport', 'borrowingBaseCash'],
    termKey: 'totalCollectionsAccountCash',
    definition:
      'Total cash that is held specifically in the collections account prior to any reserve adjustments.',
    formula: '',
  },
  {
    categories: ['borrowingBaseReport', 'borrowingBaseCash'],
    termKey: 'borrowingBaseReserveAmount',
    definition:
      'Amount to withhold from cash collections account for its final borrowing base amount.',
    formula: '',
  },
  {
    categories: ['borrowingBaseReport', 'borrowingBaseCash'],
    termKey: 'cashReserveAmount',
    definition: 'The cash reserve withheld from a collections recycle',
    formula: '',
  },
  {
    categories: ['borrowingBaseReport', 'borrowingBaseCash'],
    termKey: 'isBorrowingBaseReservePositiveExcessCollectionsOverReserve',
    definition:
      'This determines whether the borrowing base reserve contributes to the borrowing base amount.',
    formula: 'Math.max(cash - borrowingBaseReserveAmount, 0)',
  },
  {
    categories: ['borrowingBaseReport', 'borrowingBaseCash'],
    termKey: 'totalPreAdvanceCashContribution',
    definition:
      'The total amount of cash including the borrowing base reserve amount, but before the cash advance rates are applied.',
    formula:
      'Total Eligible Borrowing Base Account Cash - Borrowing Base Reserve Amount',
  },
  {
    categories: ['borrowingBaseReport', 'borrowingBaseCash'],
    termKey: 'effectiveCashAdvanceRate',
    definition:
      "The weighted average cash advance rate across the borrowing base contributing bank accounts. Each bank account's balance is used as the weight.",
    formula:
      'Total Cash Borrowing Base Amount / Total Pre Advance Cash Contribution',
  },
  {
    categories: ['borrowingBaseReport', 'borrowingBaseCash'],
    termKey: 'totalCashBorrowingBaseAmount',
    definition:
      'The final borrowing base contributions from the cash portion of the borrowing base report, after the reserve kickout and applying the cash advance rate.',
    formula:
      'Total Pre Advance Cash Contribution * Effective Cash Advance Rate',
  },
  {
    categories: ['borrowingBaseReport', 'metrics'],
    termKey: 'maximumAvailableCashAmount',
    definition:
      'The maximum cash amount of the borrowing base that can be used to recycle or paydown from the collections account.',
    formula:
      'MIN(Total Borrowing Base Amount - Current Principal Balance, Total Cash Borrowing Base Amount) / Effective Cash Advance Rate',
  },
  {
    categories: ['borrowingBaseReport', 'metrics'],
    termKey: 'maximumAvailableAmount',
    definition:
      'The maximum available amount is the upper limit of funds a borrower can draw from a credit facility.',
    formula: 'MIN(Total Borrowing Base Amount, Commitment Amount)',
  },
  {
    categories: ['borrowingBaseReport', 'metrics'],
    termKey: 'borrowingBaseAvailability',
    definition:
      'Borrowing base availability refers to the amount of funds that a borrower can access from a credit facility based on the current value of eligible collateral assets.',
    formula: 'Maximum Available Amount - Current Principal Balance',
  },
  {
    categories: ['borrowingBaseReport', 'metrics'],
    termKey: 'borrowingBaseAvailabilityRate',
    definition:
      'Borrowing base availability rate is the % amount a borrower can access from a credit facility based on the current value of eligible collateral assets.',
    formula: 'Borrowing Base Availability / Maximum Available Amount',
  },
  {
    categories: ['metrics'],
    termKey: 'effectiveAdvanceRate',
    definition:
      'This is the effective advance rate for the given borrowing base report, calculated by taking the final borrowing base from receivables and cash (after relevant kickouts and applying the configured advance rate) over the starting total assets.',
    formula: 'Total Borrowing Base Amount / Total Assets',
  },
  {
    categories: ['metrics'],
    termKey: 'currentPrincipalBalance',
    definition:
      'The outstanding loan balance. This does not include any draws or paydowns that have not yet settled.',
    formula: '',
  },
  {
    categories: ['metrics'],
    termKey: 'commitmentAmount',
    definition:
      'The commitment amount of the facility, configured in the credit agreement.',
    formula: '',
  },
  {
    categories: ['metrics'],
    termKey: 'totalFacilityUtilization',
    definition:
      'The current percentage that a facility is utilized, calculated by taking the current principal balance over the commitment amount.',
    formula: 'Current Principal Balance / Commitment Amount',
  },
  {
    categories: ['metrics'],
    termKey: 'borrowingBaseUtilization',
    definition:
      'This determines the extent to which the borrower is utilizing the available borrowing base for the given borrowing base report, calculated by taking the current principal balance over the total borrowing base amount.',
    formula: 'Current Principal Balance / Total Borrowing Base Amount',
  },
  {
    categories: ['metrics'],
    termKey: 'totalPerformingExposure',
    definition:
      'This is the total receivable exposure that is deemed to be performing, which is defined where the receivable is neither delinquent nor charged off.',
    formula: 'Exposure WHERE chargedOff = false AND delinquent = false',
  },
  {
    categories: ['metrics'],
    termKey: 'collateralCovenantLTV',
    definition:
      'The loan-to-value metric where the denominator is the pre-advance receivable borrowing base and the money already sitting in the collections account (before any advance rate is applied).',
    formula:
      'Current Principal Balance / (Total Eligible Exposure Less Concentration Kickouts + Total Collections Account Cash)',
  },
  {
    categories: ['metrics'],
    termKey: 'performingLTV',
    definition:
      'The loan-to-value metric that considers only the performing exposure of the receivables, and does not consider any eligibility or concentration kickouts.',
    formula:
      'Current Principal Balance / (Total Performing Exposure + Total Collections Account Cash)',
  },
  {
    categories: ['metrics'],
    termKey: 'netOutstandingLTV',
    definition:
      'The loan-to-value metric that looks to net out the money in the collections account (before any advance rate is applied). This is done by netting out the collections amount in the numerator as well as the denominator.',
    formula:
      '(Current Principal Balance - Total Pre Reserve Collections Account Cash) / (Eligible Exposure Less Concentration + Total Pre Reserve Collections Account Cash)',
  },
  {
    categories: ['configuration'],
    termKey: 'triggerDate',
    definition:
      'Trigger Date is the date (in ISO8601 format, e.g. 2024-05-03) that a Finley engine ran. This helps you anchor the date from which underlying source data will be pulled from. In relevant engines, you will be able to configure reporting dates and other dates relative to this trigger date.',
  },
  {
    categories: ['configuration'],
    termKey: 'reportingPeriod',
    definition:
      'Reporting Period is the unit of time used for a given dataset. The smallest unit of time available is a calendar day (eg 2024‒05‒03). You are able to report on larger units of time from there, such as a calendar week or quarter.\nFinley will take the latest day whenever it records multiple calendar days in a larger unit of time. For example, if Finley has 2024‒05‒03 and 2024‒05‒10 data for a calendar month reporting period, it will use the 2024‒05‒10 data to represent data from May.',
  },
];

const getFormulaForIntermediateCalcField = ({
  field,
  configsMap,
}: {
  field: CalcTermsIntermediateFields;
  configsMap: _.Dictionary<CreditAgreement.Config.LoanTapeField.IntermediateCalcs.IntermediateCalcContainer>;
}): string | null => {
  const config = configsMap[field];

  const formula = config.config.formula;

  // if formula for facility === "jsNull", treat as null and do not display
  const jsNullStr = '"jsNull"';

  if (formula !== jsNullStr) {
    return formula;
  }

  return null;
};

const mergeFormulas = (
  formulasToMerge: {
    chargeOffs: CalcTermsDynamicFormula[];
    delinquencies: CalcTermsDynamicFormula[];
    daysPastDue: CalcTermsDynamicFormula[];
  }[],
): CalcTermsDynamicFormulas => {
  return {
    chargeOffs: formulasToMerge.flatMap((formulas) => formulas.chargeOffs),
    delinquencies: formulasToMerge.flatMap(
      (formulas) => formulas.delinquencies,
    ),
    daysPastDue: formulasToMerge.flatMap((formulas) => formulas.daysPastDue),
  };
};

const getRequiredIntermediateFieldsFormulas = ({
  creditAgreementArtifacts,
  creditFacilities,
}: {
  creditAgreementArtifacts: CreditAgreement.DomainEntity.CreditAgreementFullArtifact[];
  creditFacilities: Pick<CreditFacilityGQLType, 'id' | 'name' | 'slug'>[];
}): CalcTermsDynamicFormulas => {
  const requiredIntermediateFieldsForAllCreditAgreements: CalcTermsDynamicFormulas[] = creditAgreementArtifacts.map(
    ({ borrowingBaseMechanics, creditFacilityId }) => {
      const creditFacility = creditFacilities.find(
        (cf) => cf.id === creditFacilityId,
      );

      finleyAssert(
        creditFacility,
        `Credit facility object must exist for given credit facility id ${creditFacilityId}`,
      );

      const formulasPerLoanProduct = borrowingBaseMechanics.calcAgentMechanics.loanProductMechanics.map(
        (lpMech) => {
          const intermediateCalcConfigsMap = _.keyBy(
            lpMech._config_.intermediateCalcConfigs,
            ({ field }) => field,
          );

          const chargeOffsFormula = getFormulaForIntermediateCalcField({
            configsMap: intermediateCalcConfigsMap,
            field: 'normalizedisChargedOff',
          });
          const delinquenciesFormula = getFormulaForIntermediateCalcField({
            configsMap: intermediateCalcConfigsMap,
            field: 'normalizedisDelinquent',
          });
          const daysPastDueFormula = getFormulaForIntermediateCalcField({
            configsMap: intermediateCalcConfigsMap,
            field: 'normalizedDaysPastDue',
          });

          const formulasForCreditFacilityAndLoanProduct = {
            chargeOffs: [
              {
                creditFacilityName: creditFacility.name,
                creditFacilityId: creditFacility.id,
                loanProductId: lpMech.entity.id,
                loanProductName: lpMech.entity.name,
                formula: chargeOffsFormula,
              },
            ],
            delinquencies: [
              {
                creditFacilityName: creditFacility.name,
                creditFacilityId: creditFacility.id,
                loanProductId: lpMech.entity.id,
                loanProductName: lpMech.entity.name,
                formula: delinquenciesFormula,
              },
            ],
            daysPastDue: [
              {
                creditFacilityName: creditFacility.name,
                creditFacilityId: creditFacility.id,
                loanProductId: lpMech.entity.id,
                loanProductName: lpMech.entity.name,
                formula: daysPastDueFormula,
              },
            ],
          };

          return formulasForCreditFacilityAndLoanProduct;
        },
      );

      // first consolidate formulas across loan products on a single facility
      return mergeFormulas(formulasPerLoanProduct);
    },
  );
  // then consolidate formulas across multiple facilities
  return mergeFormulas(requiredIntermediateFieldsForAllCreditAgreements);
};

export const getInitialCalcTermsRows = ({
  creditAgreementArtifacts,
  creditFacilities,
}: {
  creditAgreementArtifacts?:
    | CreditAgreement.DomainEntity.CreditAgreementFullArtifact[]
    | null;
  creditFacilities?:
    | Pick<CreditFacilityGQLType, 'id' | 'slug' | 'name'>[]
    | null;
}): CalcTermsRow[] => {
  if (!creditAgreementArtifacts || !creditFacilities) {
    return baseCalcTermsRows;
  }

  const rifFormulas = getRequiredIntermediateFieldsFormulas({
    creditAgreementArtifacts,
    creditFacilities,
  });

  const rifRows: CalcTermsRow[] = [
    {
      categories: ['metrics'],
      termKey: 'chargeOffs',
      definition:
        'A true/false on whether this receivable is determined to be charged off by the facility. This is derived at a facility level from the relevant receivable field that informs how many days past due it is.',
      // formula: _.join(rifFormulas.chargeOffs, '\n'),
      formula: rifFormulas.chargeOffs,
    },
    {
      categories: ['metrics'],
      termKey: 'delinquencies',
      definition:
        'A true/false on whether this receivable is determined to be delinquent by the facility. This is derived at a facility level from the relevant receivable field that informs how many days past due it is.',
      // formula: _.join(rifFormulas.delinquencies, '\n'),
      formula: rifFormulas.delinquencies,
    },
    {
      categories: ['metrics'],
      termKey: 'daysPastDue',
      definition:
        'The number of days past due of a receivable, that is derived at a facility level.',
      // formula: _.join(rifFormulas.daysPastDue, '\n'),
      formula: rifFormulas.daysPastDue,
    },
  ];

  return [...baseCalcTermsRows, ...rifRows];
};

/**
 * Given some label string that represents a calcTerms termKey
 * Return the calcTerms definition of the camelCase version of the label exists as a termKey
 */
export const getCalcTermDefinitionAndFormulaForLabel = ({
  termKeyLabel,
  creditAgreementArtifacts,
  creditFacilities,
}: {
  termKeyLabel?: string;
  creditAgreementArtifacts?:
    | CreditAgreement.DomainEntity.CreditAgreementFullArtifact[]
    | null;
  creditFacilities?:
    | Pick<CreditFacilityGQLType, 'id' | 'slug' | 'name'>[]
    | null;
}):
  | {
      definition: string | undefined;
      formula: Util.Maybe<string | CalcTermsDynamicFormula[]>;
    }
  | undefined => {
  if (!termKeyLabel) {
    return undefined;
  }

  const calcTermsRows = getInitialCalcTermsRows({
    creditAgreementArtifacts,
    creditFacilities,
  });

  const calcTermRow = calcTermsRows.find(
    (row) => _.camelCase(row.termKey) === _.camelCase(termKeyLabel),
  );

  return { definition: calcTermRow?.definition, formula: calcTermRow?.formula };
};
